import { TRADEABLE_PAIRS } from '../constants/pairs';
import consts from '../constant';

const RECONNECT_DELAY_MS = 1400;
const MIN_PRICE = 0.00000001;
const TICK_PACE_MS = 450; // Pace ticks to ~450ms for controlled, calm live line flow
const STALE_FEED_CHECK_MS = 480;
const MIN_STALE_TICK_GAP_MS = 520;
const SYNTH_DRIFT_MIN = 0.00009;
const SYNTH_DRIFT_MAX = 0.00042;
const SYNTH_NOISE_RATIO = 0.00022;
const SYNTH_DIRECTION_FLIP_CHANCE = 0.14;

function buildSocketBaseUrls() {
  const urls = [];
  if (consts?.socketUrl) {
    let wsBase = String(consts.socketUrl).replace(/^http/, 'ws');
    if (wsBase.endsWith('/')) wsBase = wsBase.slice(0, -1);
    urls.push(`${wsBase}/binance-feed`);
  }
  urls.push(
    'wss://stream.binance.com:9443/ws',
    'wss://data-stream.binance.vision/ws',
    'wss://stream.binance.com/ws',
    'wss://stream.binance.us:9443/ws'
  );
  return urls;
}

const BINANCE_SOCKET_BASE_URLS = buildSocketBaseUrls();

const feedCache = new Map();

export class BinanceTradeFeed {
  constructor(config) {
    const symbolKey = config?.symbol?.toUpperCase();
    if (feedCache?.has(symbolKey)) {
      const cached = feedCache.get(symbolKey);
      cached.refCount++;
      return cached;
    }

    this.subscribers = new Set();
    this.symbol = symbolKey;

    const fallbackPair = TRADEABLE_PAIRS.find(p => p.symbol === symbolKey);
    this.previousPrice = Number.isFinite(config.startPrice) && config.startPrice > 0
      ? config.startPrice
      : (fallbackPair?.fallbackPrice ?? 100);

    this.heartbeatMs = Number.isFinite(config.heartbeatMs) && config.heartbeatMs >= 250
      ? Math.floor(config.heartbeatMs)
      : 0;
    this.socket = null;
    this.reconnectTimer = null;
    this.heartbeatTimer = null;
    this.staleTickTimer = null;
    this.lastEmittedAtMs = 0;
    this.lastStreamTickAtMs = 0;
    this.lastTickTime = Math.round((Date.now() / 1000) * 10) / 10;
    this.pendingTick = null;
    this.pendingTickTimer = null;
    this.syntheticDirection = Math.random() > 0.5 ? 1 : -1;
    this.enableSyntheticFallback = config.enableSyntheticFallback !== false;
    this.socketBaseUrls = Array.isArray(config.socketBaseUrls) && config.socketBaseUrls.length > 0
      ? config.socketBaseUrls
      : BINANCE_SOCKET_BASE_URLS;
    this.socketBaseIndex = 0;
    this.isStopped = true;
    this.refCount = 1;

    feedCache.set(this.symbol, this);
  }
  subscribe(subscriber) {
    this.subscribers.add(subscriber);
    return () => {
      this.subscribers.delete(subscriber);
    };
  }
  start() {
    if (!this.isStopped) {
      return;
    }
    this.isStopped = false;
    this.startHeartbeat();
    if (this.enableSyntheticFallback) {
      this.startStaleTicker();
    }
    this.connect();
  }
  stop() {
    this.refCount--;
    if (this.refCount > 0) {
      return;
    }

    this.isStopped = true;
    this.clearReconnectTimer();
    this.clearHeartbeat();
    this.clearStaleTicker();
    if (this.pendingTickTimer !== null) {
      clearTimeout(this.pendingTickTimer);
      this.pendingTickTimer = null;
    }
    this.pendingTick = null;
    if (this.socket && (this.socket.readyState === WebSocket.CONNECTING || this.socket.readyState === WebSocket.OPEN)) {
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
      this.socket.close();
    }
    this.socket = null;
    feedCache.delete(this.symbol);
  }
  connect() {
    if (this.isStopped) {
      return;
    }
    if (this.socket && (this.socket.readyState === WebSocket.CONNECTING || this.socket.readyState === WebSocket.OPEN)) {
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.onmessage = null;
      this.socket.close();
    }
    const stream = `${this?.symbol?.toLowerCase()}@trade`;
    const socketBase = this.socketBaseUrls[this.socketBaseIndex] ?? BINANCE_SOCKET_BASE_URLS[0];
    const socketUrl = `${socketBase}/${stream}`;
    const socket = new WebSocket(socketUrl);
    this.socket = socket;
    socket.onopen = () => {
      this.lastStreamTickAtMs = Date.now();
    };
    socket.onmessage = (event) => {
      const data = safeJsonParse(event.data);
      const tick = toStreamTick(data, this.previousPrice);
      if (!tick) {
        return;
      }
      this.lastStreamTickAtMs = Date.now();
      const now = Date.now();
      if (now - this.lastEmittedAtMs >= TICK_PACE_MS) {
        if (this.pendingTickTimer !== null) {
          clearTimeout(this.pendingTickTimer);
          this.pendingTickTimer = null;
        }
        this.pendingTick = null;
        this.emitTick(tick);
      } else {
        this.pendingTick = tick;
        if (!this.pendingTickTimer) {
          const waitMs = Math.max(16, TICK_PACE_MS - (now - this.lastEmittedAtMs));
          this.pendingTickTimer = setTimeout(() => {
            this.pendingTickTimer = null;
            if (this.pendingTick && !this.isStopped) {
              const toEmit = this.pendingTick;
              this.pendingTick = null;
              this.emitTick(toEmit);
            }
          }, waitMs);
        }
      }
    };
    socket.onerror = () => {
      socket.close();
    };
    socket.onclose = () => {
      if (this.isStopped) {
        return;
      }
      this.socketBaseIndex = (this.socketBaseIndex + 1) % this.socketBaseUrls.length;
      this.scheduleReconnect();
    };
  }
  emitTick(tick) {
    const normalizedPrice = roundPriceByMagnitude(Math.max(MIN_PRICE, tick.price));
    const direction = normalizedPrice >= this.previousPrice ? 'up' : 'down';
    const nowSec = Date.now() / 1000;
    const subSecondStep = 0.3;
    const candidateTime = Number.isFinite(tick?.time)
      ? Math.round(tick.time * 10) / 10
      : Math.round(nowSec * 10) / 10;
    const minTime = Math.round((this.lastTickTime + subSecondStep) * 10) / 10;
    const nextTickTime = candidateTime > this.lastTickTime ? candidateTime : minTime;
    this.previousPrice = normalizedPrice;
    this.lastEmittedAtMs = Date.now();
    this.lastTickTime = nextTickTime;
    const normalizedOpen = Number.isFinite(tick.open)
      ? roundPriceByMagnitude(Math.max(MIN_PRICE, tick.open))
      : normalizedPrice;
    const normalizedClose = Number.isFinite(tick.close)
      ? roundPriceByMagnitude(Math.max(MIN_PRICE, tick.close))
      : normalizedPrice;
    const normalizedHigh = Number.isFinite(tick.high)
      ? roundPriceByMagnitude(Math.max(MIN_PRICE, tick.high))
      : Math.max(normalizedOpen, normalizedClose, normalizedPrice);
    const normalizedLow = Number.isFinite(tick.low)
      ? roundPriceByMagnitude(Math.max(MIN_PRICE, tick.low))
      : Math.min(normalizedOpen, normalizedClose, normalizedPrice);
    this.subscribers.forEach((subscriber) => subscriber({
      price: normalizedPrice,
      time: nextTickTime,
      direction: tick.direction ?? direction,
      sourceTimeMs: Number.isFinite(tick.sourceTimeMs) ? tick.sourceTimeMs : undefined,
      open: normalizedOpen,
      high: Math.max(normalizedHigh, normalizedOpen, normalizedClose, normalizedPrice),
      low: Math.min(normalizedLow, normalizedOpen, normalizedClose, normalizedPrice),
      close: normalizedClose
    }));
  }
  scheduleReconnect() {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, RECONNECT_DELAY_MS);
  }
  clearReconnectTimer() {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
  startHeartbeat() {
    if (this.heartbeatMs <= 0 || this.heartbeatTimer !== null) {
      return;
    }
    this.heartbeatTimer = setInterval(() => {
      if (this.isStopped || !Number.isFinite(this.previousPrice)) {
        return;
      }
      const nowMs = Date.now();
      if (nowMs - this.lastEmittedAtMs < this.heartbeatMs * 0.84) {
        return;
      }
      const nextTime = Math.max(
        Math.round((Date.now() / 1000) * 10) / 10,
        Math.round((this.lastTickTime + 0.3) * 10) / 10
      );
      this.emitTick({
        price: this.previousPrice,
        time: nextTime,
        direction: 'up'
      });
    }, this.heartbeatMs);
  }
  clearHeartbeat() {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
  startStaleTicker() {
    if (this.staleTickTimer !== null) {
      return;
    }
    this.staleTickTimer = setInterval(() => {
      if (this.isStopped || !Number.isFinite(this.previousPrice)) {
        return;
      }
      const nowMs = Date.now();
      const staleThresholdMs = Math.max(MIN_STALE_TICK_GAP_MS, this.heartbeatMs > 0 ? this.heartbeatMs * 2 : 0);
      // Use real-stream recency (not heartbeat recency) so heartbeat won't block fallback drift.
      if (this.lastStreamTickAtMs > 0 && nowMs - this.lastStreamTickAtMs < staleThresholdMs) {
        return;
      }
      if (Math.random() < SYNTH_DIRECTION_FLIP_CHANCE) {
        this.syntheticDirection *= -1;
      }
      const driftRatio = SYNTH_DRIFT_MIN + (Math.random() * (SYNTH_DRIFT_MAX - SYNTH_DRIFT_MIN));
      const noiseRatio = (Math.random() - 0.5) * SYNTH_NOISE_RATIO * 2;
      const moveRatio = (driftRatio * this.syntheticDirection) + noiseRatio;
      const nextPrice = Math.max(MIN_PRICE, this.previousPrice + (this.previousPrice * moveRatio));
      const nextTime = Math.max(
        Math.round((nowMs / 1000) * 10) / 10,
        Math.round((this.lastTickTime + 0.3) * 10) / 10
      );
      this.emitTick({
        price: nextPrice,
        time: nextTime,
        direction: nextPrice >= this.previousPrice ? 'up' : 'down',
        sourceTimeMs: nowMs,
        open: this.previousPrice,
        high: Math.max(this.previousPrice, nextPrice),
        low: Math.min(this.previousPrice, nextPrice),
        close: nextPrice
      });
    }, STALE_FEED_CHECK_MS);
  }
  clearStaleTicker() {
    if (this.staleTickTimer !== null) {
      clearInterval(this.staleTickTimer);
      this.staleTickTimer = null;
    }
  }
}

function toStreamTick(raw, previousPrice) {
  if (!raw) {
    return null;
  }
  const payload = raw?.data ?? raw;
  if (!payload) {
    return null;
  }

  // Trade / aggTrade stream payload.
  const tradePrice = Number(payload.p);
  if (Number.isFinite(tradePrice)) {
    const normalizedTrade = roundPriceByMagnitude(Math.max(MIN_PRICE, tradePrice));
    const tradeTimeMs = Number(payload.T ?? payload.E);
    const subSecondTime = Number.isFinite(tradeTimeMs)
      ? Math.round((tradeTimeMs / 1000) * 10) / 10
      : Math.round((Date.now() / 1000) * 10) / 10;
    return {
      price: normalizedTrade,
      time: subSecondTime,
      direction: normalizedTrade >= previousPrice ? 'up' : 'down',
      sourceTimeMs: Number.isFinite(tradeTimeMs) ? tradeTimeMs : undefined,
      open: normalizedTrade,
      high: normalizedTrade,
      low: normalizedTrade,
      close: normalizedTrade
    };
  }

  // Keep support for kline payloads if endpoint changes.
  const kline = payload.k;
  if (!kline) {
    return null;
  }
  const openPrice = Number(kline.o);
  const highPrice = Number(kline.h);
  const lowPrice = Number(kline.l);
  const closePrice = Number(kline.c);
  const klineOpenMs = Number(kline.t);
  if (!Number.isFinite(openPrice) ||
    !Number.isFinite(highPrice) ||
    !Number.isFinite(lowPrice) ||
    !Number.isFinite(closePrice)) {
    return null;
  }
  const normalizedOpen = roundPriceByMagnitude(Math.max(MIN_PRICE, openPrice));
  const normalizedClose = roundPriceByMagnitude(Math.max(MIN_PRICE, closePrice));
  const normalizedHigh = roundPriceByMagnitude(Math.max(normalizedOpen, normalizedClose, Math.max(MIN_PRICE, highPrice)));
  const normalizedLow = roundPriceByMagnitude(Math.min(normalizedOpen, normalizedClose, Math.max(MIN_PRICE, lowPrice)));
  return {
    price: normalizedClose,
    time: Number.isFinite(klineOpenMs) ? Math.floor(klineOpenMs / 1000) : Math.floor(Date.now() / 1000),
    direction: normalizedClose >= previousPrice ? 'up' : 'down',
    sourceTimeMs: Number.isFinite(klineOpenMs) ? klineOpenMs : undefined,
    open: normalizedOpen,
    high: normalizedHigh,
    low: normalizedLow,
    close: normalizedClose
  };
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  }
  catch (_error) {
    return null;
  }
}
function roundPriceByMagnitude(value) {
  if (value >= 1000) return Math.round(value * 100) / 100;
  if (value >= 10) return Math.round(value * 1000) / 1000;
  if (value >= 1) return Math.round(value * 10000) / 10000;
  if (value >= 0.1) return Math.round(value * 100000) / 100000;
  if (value >= 0.01) return Math.round(value * 1000000) / 1000000;
  if (value >= 0.0001) return Math.round(value * 10000000) / 10000000;
  return Math.round(value * 100000000) / 100000000;
}
